import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  configApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AKS_SERVER_APP_SCOPE } from '../kubernetes/api';
import { v4 as uuidv4 } from 'uuid';

import { Composer, type ModelOption } from './components/Composer';
import {
  DropOverlay,
  imageFiles,
  useImageAttachments,
} from './components/Attachments';
import { MessageThread } from './components/MessageThread';
import {
  extractArtifacts,
  OperationsCanvas,
} from './components/OperationsCanvas';
import { ConversationControls } from './components/ConversationControls';
import { noraDownload, noraRequest } from './api';
import { hydrateMessages } from './runtime/hydrate';
import { consumeSSE } from './runtime/sseConsumer';
import {
  NoraProvider,
  useNora,
  useRuntimeEventDispatch,
} from './store/noraStore';
import {
  ANIM,
  COLOR,
  FONT,
  injectGlobalStyles,
  useIsMobile,
} from './styles/tokens';
import type {
  ChatMessage,
  ConversationMetadata,
  NoraActiveContext,
} from './types';

const CHAT_RAIL_WIDTH = 64;

function contextFromParams(params: URLSearchParams): NoraActiveContext {
  const value = (key: string) => params.get(key)?.trim() || undefined;
  return {
    cluster: value('cluster'),
    namespace: value('namespace'),
    kind: value('kind'),
    name: value('name'),
    entityRef: value('entityRef'),
  };
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function validatedReturnTo(params: URLSearchParams): string | null {
  const value = params.get('returnTo');
  return value?.startsWith('/kubernetes?') || value === '/kubernetes'
    ? value
    : null;
}

function conversationParams(
  id: string,
  currentParams: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams({ conversation: id });
  const returnTo = validatedReturnTo(currentParams);
  if (returnTo) next.set('returnTo', returnTo);
  for (const key of ['cluster', 'namespace', 'kind', 'name', 'entityRef']) {
    const value = currentParams.get(key);
    if (value) next.set(key, value);
  }
  return next;
}

// The backend persists conversations by `mode` ('private' | 'shared' |
// 'temporary') and keys the id as `conversationId` (or `id` on the list
// endpoint). The UI works in terms of `visibility` + `temporary` and always
// keys off `id` — translate at the boundary rather than threading the
// backend's vocabulary through every component.
interface BackendConversation {
  id?: string;
  conversationId?: string;
  mode?: 'private' | 'shared' | 'temporary';
  retentionDays?: number | null;
  parentConversationId?: string | null;
  childConversationIds?: string[];
}

function toConversationMetadata(
  raw: BackendConversation,
  fallbackId: string,
): ConversationMetadata {
  return {
    id: raw.id ?? raw.conversationId ?? fallbackId,
    visibility: raw.mode === 'shared' ? 'shared' : 'private',
    temporary: raw.mode === 'temporary',
    retentionDays: raw.retentionDays ?? null,
    parentConversationId: raw.parentConversationId ?? undefined,
    childConversationIds: raw.childConversationIds ?? undefined,
  };
}

interface ConversationSummary {
  id: string;
  title: string;
  lastAt: string;
  visibility?: 'private' | 'shared';
  temporary?: boolean;
  retentionDays?: number | null;
  parentConversationId?: string;
  childConversationIds?: string[];
  branchCount?: number;
}

const NewChatIcon = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const SearchIcon = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const NoraLogo = (
  <svg width="26" height="26" viewBox="0 0 32 32" aria-hidden="true">
    <defs>
      <linearGradient id="nora-gradient" x1="4" y1="28" x2="28" y2="4">
        <stop offset="0" stopColor="#34a853" />
        <stop offset="0.35" stopColor="#4285f4" />
        <stop offset="0.68" stopColor="#9b72cb" />
        <stop offset="1" stopColor="#ea4335" />
      </linearGradient>
    </defs>
    <path
      fill="url(#nora-gradient)"
      d="M16 2c1.5 8.4 5.6 12.5 14 14-8.4 1.5-12.5 5.6-14 14-1.5-8.4-5.6-12.5-14-14C10.4 14.5 14.5 10.4 16 2Z"
    />
  </svg>
);

function relativeDate(value: string): string {
  const date = new Date(value);
  const now = new Date();
  const dateDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
  const today = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const days = Math.round((today - dateDay) / 86_400_000);

  if (days <= 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (days === 1) return 'Yesterday';

  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
  };
  if (date.getFullYear() !== now.getFullYear()) options.year = 'numeric';
  return date.toLocaleDateString([], options);
}

function IconButton({
  active = false,
  label,
  onClick,
  children,
}: {
  active?: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      style={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: active ? COLOR.bgOverlay : 'transparent',
        border: 'none',
        color: active ? COLOR.textPrimary : COLOR.textSecondary,
        cursor: 'pointer',
        padding: 0,
      }}
    >
      {children}
    </button>
  );
}

function ConversationSearch({
  conversations,
  error,
  query,
  onClose,
  onOpen,
  onQueryChange,
}: {
  conversations: ConversationSummary[];
  error: string | null;
  query: string;
  onClose: () => void;
  onOpen: (id: string) => void;
  onQueryChange: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = query.trim()
    ? conversations.filter(conversation =>
        conversation.title.toLowerCase().includes(query.trim().toLowerCase()),
      )
    : conversations;

  return (
    <div
      className="nora-scroll"
      style={{ flex: 1, overflowY: 'auto', padding: '48px 24px' }}
      onKeyDown={event => {
        if (event.key === 'Escape') onClose();
      }}
    >
      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            background: COLOR.bg,
            border: `1px solid ${COLOR.border}`,
            borderRadius: 26,
            padding: '13px 20px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
          }}
        >
          <span style={{ color: COLOR.textSecondary, display: 'flex' }}>
            {SearchIcon}
          </span>
          <input
            ref={inputRef}
            value={query}
            onChange={event => onQueryChange(event.target.value)}
            placeholder="Search chats"
            aria-label="Search chats"
            style={{
              flex: 1,
              minWidth: 0,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: COLOR.textPrimary,
              caretColor: COLOR.blue,
              fontFamily: FONT.sans,
              fontSize: FONT.size.md,
            }}
          />
          {query && (
            <button
              type="button"
              onClick={() => onQueryChange('')}
              aria-label="Clear search"
              style={{
                background: 'transparent',
                border: 'none',
                color: COLOR.textMuted,
                cursor: 'pointer',
                fontSize: 18,
                padding: 0,
              }}
            >
              ×
            </button>
          )}
        </div>

        <div
          style={{
            margin: '32px 4px 10px',
            color: COLOR.textSecondary,
            fontFamily: FONT.sans,
            fontSize: FONT.size.sm,
            fontWeight: 600,
          }}
        >
          {query.trim() ? `Results (${filtered.length})` : 'Recent'}
        </div>

        {error && (
          <div
            role="alert"
            style={{
              margin: '12px 4px',
              color: COLOR.red,
              fontFamily: FONT.sans,
              fontSize: FONT.size.sm,
            }}
          >
            {error}
          </div>
        )}

        {!error && filtered.length === 0 && (
          <div
            style={{
              padding: '12px 4px',
              color: COLOR.textInactive,
              fontFamily: FONT.sans,
              fontSize: FONT.size.sm,
            }}
          >
            {query.trim()
              ? 'No chats match your search.'
              : 'Start a conversation and it will appear here.'}
          </div>
        )}

        {filtered.map(conversation => (
          <button
            type="button"
            key={conversation.id}
            onClick={() => onOpen(conversation.id)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              padding: '13px 16px',
              borderRadius: 12,
              background: 'transparent',
              border: 'none',
              color: COLOR.textPrimary,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontFamily: FONT.sans,
                fontSize: FONT.size.md,
              }}
            >
              {conversation.title}
            </span>
            <span
              style={{
                flexShrink: 0,
                color: COLOR.textMuted,
                fontFamily: FONT.sans,
                fontSize: FONT.size.sm,
              }}
            >
              {relativeDate(conversation.lastAt)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function NoraChat({ embedded }: { embedded: boolean }) {
  const { state, dispatch } = useNora();
  const dispatchEvent = useRuntimeEventDispatch();
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  const isMobile = useIsMobile();
  const abortRef = useRef<AbortController | null>(null);
  const conversationId = useRef('');
  const handledConversationId = useRef('');
  const conversationLoadSequence = useRef(0);
  const dragDepth = useRef(0);

  const [view, setView] = useState<'chat' | 'search'>('chat');
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [conversationError, setConversationError] = useState<string | null>(
    null,
  );
  const [conversationLoading, setConversationLoading] = useState(false);
  const [models, setModels] = useState<ModelOption[]>([]);
  const [modelError, setModelError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [firstName, setFirstName] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [conversationMetadata, setConversationMetadata] =
    useState<ConversationMetadata | null>(null);
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [activeContext, setActiveContext] = useState<NoraActiveContext>(() =>
    contextFromParams(searchParams),
  );
  const {
    attachments,
    addFiles,
    remove: removeAttachment,
    clear: clearAttachments,
    ready: readyImages,
  } = useImageAttachments();

  const backendBase = configApi.getOptionalString('backend.baseUrl') ?? '';
  const noraBase = `${backendBase}/api/nora`;
  const artifacts = useMemo(
    () => extractArtifacts(state.messages),
    [state.messages],
  );

  useEffect(() => {
    injectGlobalStyles();
  }, []);

  useEffect(() => {
    identityApi
      .getProfileInfo()
      .then(profile => {
        const name = profile.displayName?.split(/[\s.]/)[0] ?? '';
        setFirstName(name.charAt(0).toUpperCase() + name.slice(1));
      })
      .catch(() => setFirstName(''));
  }, [identityApi]);

  useEffect(() => {
    const handleCopy = (event: MouseEvent) => {
      const button = (event.target as Element).closest(
        '.nora-copy-btn',
      ) as HTMLElement | null;
      const code = button
        ?.closest('.nora-code-block')
        ?.querySelector('pre code');
      if (!button || !code) return;

      navigator.clipboard
        .writeText((code as HTMLElement).innerText)
        .then(() => {
          const label = button.querySelector('span');
          if (!label) return;
          const previous = label.textContent;
          label.textContent = 'Copied';
          window.setTimeout(() => {
            label.textContent = previous;
          }, 1500);
        })
        .catch(() => undefined);
    };

    document.addEventListener('click', handleCopy);
    return () => document.removeEventListener('click', handleCopy);
  }, []);

  const fetchConversations = useCallback(async () => {
    setHistoryError(null);
    try {
      const body = await noraRequest<{
        conversations: Array<ConversationSummary & BackendConversation>;
      }>(identityApi, noraBase, '/conversations');
      setConversations(
        body.conversations.map(item => ({
          ...item,
          visibility: item.mode === 'shared' ? 'shared' : 'private',
          temporary: item.mode === 'temporary',
        })),
      );
    } catch (error) {
      setHistoryError(error instanceof Error ? error.message : String(error));
    }
  }, [identityApi, noraBase]);

  const fetchModels = useCallback(async () => {
    setModelError(null);
    try {
      const body = await noraRequest<{
        defaultModel?: unknown;
        models?: unknown;
      }>(identityApi, noraBase, '/models');
      if (!Array.isArray(body.models) || body.models.length === 0) {
        throw new Error('No Nora models are configured');
      }

      const availableModels = body.models.filter(
        (entry): entry is ModelOption =>
          typeof entry === 'object' &&
          entry !== null &&
          typeof (entry as ModelOption).id === 'string' &&
          typeof (entry as ModelOption).label === 'string',
      );
      if (availableModels.length === 0) {
        throw new Error('Nora model configuration is invalid');
      }

      const defaultModel =
        typeof body.defaultModel === 'string' &&
        availableModels.some(entry => entry.id === body.defaultModel)
          ? body.defaultModel
          : availableModels[0].id;
      setModels(availableModels);
      setSelectedModel(current =>
        availableModels.some(entry => entry.id === current)
          ? current
          : defaultModel,
      );
    } catch (error) {
      setModels([]);
      setSelectedModel('');
      setModelError(error instanceof Error ? error.message : String(error));
    }
  }, [identityApi, noraBase]);

  useEffect(() => {
    fetchConversations();
    fetchModels();
  }, [fetchConversations, fetchModels]);

  const stopResponse = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    dispatchEvent({ type: 'turn.done', content: '' });
  }, [dispatchEvent]);

  const startNewConversation = useCallback(() => {
    conversationLoadSequence.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    dispatchEvent({ type: 'turn.done', content: '' });
    const id = uuidv4();
    conversationId.current = id;
    handledConversationId.current = id;
    clearAttachments();
    setConversationError(null);
    setConversationLoading(false);
    setConversationMetadata({
      id,
      visibility: 'private',
      temporary: false,
      retentionDays: null,
    });
    setActiveContext({});
    dispatch({ type: 'NEW_CONVERSATION' });
    setView('chat');
    setSearchParams(conversationParams(id, new URLSearchParams(searchKey)));
  }, [clearAttachments, dispatch, dispatchEvent, searchKey, setSearchParams]);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        startNewConversation();
      }
    };
    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, [startNewConversation]);

  const loadConversation = useCallback(
    async (id: string, shareToken?: string) => {
      const sequence = ++conversationLoadSequence.current;
      setConversationLoading(true);
      try {
        const body = await noraRequest<{
          messages: Parameters<typeof hydrateMessages>[0];
          conversation?: BackendConversation;
        }>(identityApi, noraBase, `/conversations/${id}`, {
          headers: shareToken ? { 'x-nora-share-token': shareToken } : {},
        });
        if (sequence !== conversationLoadSequence.current) return;
        conversationId.current = id;
        dispatch({
          type: 'LOAD_CONVERSATION',
          messages: hydrateMessages(body.messages),
        });
        setConversationMetadata(
          body.conversation
            ? toConversationMetadata(body.conversation, id)
            : {
                id,
                visibility: 'private',
                temporary: false,
                retentionDays: null,
              },
        );
        setView('chat');
      } catch (error) {
        if (sequence !== conversationLoadSequence.current) return;
        setConversationError(
          error instanceof Error ? error.message : String(error),
        );
      } finally {
        if (sequence === conversationLoadSequence.current) {
          setConversationLoading(false);
        }
      }
    },
    [dispatch, identityApi, noraBase],
  );

  useEffect(() => {
    const currentParams = new URLSearchParams(searchKey);
    const requestedId = currentParams.get('conversation')?.trim() ?? '';
    setActiveContext(contextFromParams(currentParams));

    if (!UUID_PATTERN.test(requestedId)) {
      const id = uuidv4();
      currentParams.set('conversation', id);
      conversationId.current = id;
      handledConversationId.current = id;
      setConversationMetadata({
        id,
        visibility: 'private',
        temporary: false,
        retentionDays: null,
      });
      setSearchParams(currentParams, { replace: true });
      return;
    }

    if (handledConversationId.current === requestedId) return;
    handledConversationId.current = requestedId;
    conversationId.current = requestedId;
    abortRef.current?.abort();
    abortRef.current = null;
    dispatchEvent({ type: 'turn.done', content: '' });
    clearAttachments();
    dispatch({ type: 'NEW_CONVERSATION' });
    setConversationError(null);
    setView('chat');
    void loadConversation(
      requestedId,
      currentParams.get('share')?.trim() || undefined,
    );
  }, [
    clearAttachments,
    dispatch,
    dispatchEvent,
    loadConversation,
    searchKey,
    setSearchParams,
  ]);

  useEffect(() => {
    const receiveContext = (event: MessageEvent) => {
      if (
        event.origin !== window.location.origin ||
        !event.data ||
        event.data.type !== 'nora.context' ||
        typeof event.data.context !== 'object'
      ) {
        return;
      }
      const incoming = event.data.context as Record<string, unknown>;
      const next: NoraActiveContext = {};
      for (const key of [
        'cluster',
        'namespace',
        'kind',
        'name',
        'entityRef',
      ] as const) {
        if (typeof incoming[key] === 'string' && incoming[key].trim()) {
          next[key] = incoming[key].trim();
        }
      }
      setActiveContext(next);
      const params = new URLSearchParams(window.location.search);
      for (const key of [
        'cluster',
        'namespace',
        'kind',
        'name',
        'entityRef',
      ] as const) {
        if (next[key]) params.set(key, next[key]!);
        else params.delete(key);
      }
      setSearchParams(params, { replace: true });
    };
    window.addEventListener('message', receiveContext);
    return () => window.removeEventListener('message', receiveContext);
  }, [setSearchParams]);

  const openConversation = useCallback(
    (id: string) => {
      if (!UUID_PATTERN.test(id)) return;
      if (id === conversationId.current) {
        setView('chat');
        return;
      }
      setSearchParams(conversationParams(id, new URLSearchParams(searchKey)));
    },
    [searchKey, setSearchParams],
  );

  // Approve/cancel a pending mutating tool call (k8s_restart_rollout,
  // k8s_scale). Updates local state immediately so the button feedback is
  // instant, then POSTs the decision — bound to this exact toolUseId +
  // conversation — to the backend, which is the only thing that actually
  // unblocks tool.call(). If the POST fails, the backend's own pending
  // confirm still times out safely on its own, so the UI is never stuck.
  const respondToPermission = useCallback(
    async (toolUseId: string, approved: boolean) => {
      dispatch({ type: 'TOOL_PERMISSION_DECIDE', toolUseId, approved });
      try {
        const { token } = await identityApi.getCredentials();
        const response = await fetch(`${noraBase}/tool-permission`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            toolUseId,
            conversationId: conversationId.current,
            approved,
          }),
        });
        if (!response.ok) {
          throw new Error(`Could not send decision (${response.status})`);
        }
      } catch (error) {
        dispatch({ type: 'TOOL_PERMISSION_FAILED', toolUseId });
        dispatch({
          type: 'ERROR',
          message:
            error instanceof Error
              ? `Failed to send approval decision: ${error.message}`
              : 'Failed to send approval decision',
        });
      }
    },
    [dispatch, identityApi, noraBase],
  );

  const sendMessage = useCallback(
    async (text: string) => {
      const targetConversationId = conversationId.current;
      const images = readyImages;
      const userMessageId = uuidv4();
      clearAttachments();

      setConversations(current => {
        const title =
          text.trim() ||
          `${images.length} attached image${images.length === 1 ? '' : 's'}`;
        const summary = {
          id: targetConversationId,
          title,
          lastAt: new Date().toISOString(),
        };
        return [
          summary,
          ...current.filter(item => item.id !== targetConversationId),
        ];
      });

      dispatch({
        type: 'USER_MESSAGE',
        id: userMessageId,
        text,
        images,
      });

      abortRef.current?.abort();
      const abortController = new AbortController();
      abortRef.current = abortController;

      try {
        const { token } = await identityApi.getCredentials();
        // The AKS token lets Nora inspect the clusters as the signed-in
        // user (same token the Kubernetes page uses). Chat still works
        // without it — the backend simply disables the Kubernetes tools.
        let k8sToken: string | undefined;
        try {
          k8sToken =
            await microsoftAuthApi.getAccessToken(AKS_SERVER_APP_SCOPE);
        } catch {
          k8sToken = undefined;
        }
        const response = await fetch(`${noraBase}/chat`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...(k8sToken
              ? { 'X-Kubernetes-Authorization': `Bearer ${k8sToken}` }
              : {}),
          },
          body: JSON.stringify({
            message: text,
            messageId: userMessageId,
            conversationId: targetConversationId,
            model: selectedModel || undefined,
            images: images.length ? images : undefined,
            context:
              Object.keys(activeContext).length > 0 ? activeContext : undefined,
          }),
          signal: abortController.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`Nora could not respond (${response.status})`);
        }

        await consumeSSE(response.body, dispatchEvent, abortController.signal);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError')
          return;
        dispatch({
          type: 'ERROR',
          message: error instanceof Error ? error.message : String(error),
        });
      } finally {
        if (abortRef.current === abortController) abortRef.current = null;
      }
    },
    [
      clearAttachments,
      dispatch,
      dispatchEvent,
      identityApi,
      microsoftAuthApi,
      noraBase,
      readyImages,
      selectedModel,
      activeContext,
    ],
  );

  const branchFromMessage = useCallback(
    async (
      message: ChatMessage,
      options: {
        message?: string;
        retry: boolean;
        continueWith?: string;
        throughMessage?: ChatMessage;
      },
    ) => {
      if (state.streaming || conversationLoading) return;
      setConversationLoading(true);
      setConversationError(null);
      try {
        const anchor = options.throughMessage;
        if (!anchor) {
          // Editing/retrying the first turn — there is no prior message to
          // fork from, so just start a clean conversation with the text.
          startNewConversation();
          if (options.continueWith) await sendMessage(options.continueWith);
          return;
        }
        const anchorId = anchor.backendId ?? anchor.id;
        if (!UUID_PATTERN.test(anchorId)) {
          throw new Error(
            'This message has not finished saving yet — try again in a moment',
          );
        }
        const body = await noraRequest<{
          conversation?: { conversationId: string };
        }>(
          identityApi,
          noraBase,
          `/conversations/${conversationId.current}/branches`,
          {
            method: 'POST',
            body: JSON.stringify({ sourceMessageId: anchorId }),
          },
        );
        const id = body.conversation?.conversationId;
        if (!id || !UUID_PATTERN.test(id)) {
          throw new Error('Nora returned an invalid branch conversation ID');
        }
        conversationLoadSequence.current += 1;
        conversationId.current = id;
        handledConversationId.current = id;
        clearAttachments();
        dispatch({ type: 'NEW_CONVERSATION' });
        setSearchParams(conversationParams(id, new URLSearchParams(searchKey)));
        await loadConversation(id);
        if (options.continueWith) await sendMessage(options.continueWith);
        void fetchConversations();
      } catch (error) {
        setConversationError(
          error instanceof Error ? error.message : String(error),
        );
      } finally {
        setConversationLoading(false);
      }
    },
    [
      clearAttachments,
      conversationLoading,
      dispatch,
      fetchConversations,
      identityApi,
      loadConversation,
      noraBase,
      searchKey,
      sendMessage,
      setSearchParams,
      startNewConversation,
      state.streaming,
    ],
  );

  const updateConversationSettings = useCallback(
    async (settings: {
      visibility: 'private' | 'shared';
      temporary: boolean;
      retentionDays: number | null;
    }) => {
      const mode = settings.temporary ? 'temporary' : settings.visibility;
      await noraRequest<unknown>(
        identityApi,
        noraBase,
        `/conversations/${conversationId.current}/settings`,
        {
          method: 'PATCH',
          body: JSON.stringify({ mode, retentionDays: settings.retentionDays }),
        },
      );
      setConversationMetadata(current =>
        current ? { ...current, ...settings } : null,
      );
      void fetchConversations();
    },
    [fetchConversations, identityApi, noraBase],
  );

  const shareConversation = useCallback(async () => {
    const body = await noraRequest<{
      metadata?: BackendConversation;
      shareToken?: string;
    }>(
      identityApi,
      noraBase,
      `/conversations/${conversationId.current}/share`,
      { method: 'POST' },
    );
    if (!body.shareToken) throw new Error('Nora did not return a share link');
    const url = new URL(window.location.href);
    url.searchParams.set('conversation', conversationId.current);
    url.searchParams.set('share', body.shareToken);
    await navigator.clipboard.writeText(url.toString());
    setConversationMetadata(current =>
      body.metadata
        ? toConversationMetadata(body.metadata, conversationId.current)
        : current
        ? { ...current, visibility: 'shared' }
        : null,
    );
    window.alert('Share link copied to the clipboard.');
  }, [identityApi, noraBase]);

  const exportConversation = useCallback(async () => {
    const blob = await noraDownload(
      identityApi,
      noraBase,
      `/conversations/${conversationId.current}/export`,
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `nora-conversation-${conversationId.current}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [identityApi, noraBase]);

  const deleteConversation = useCallback(async () => {
    await noraRequest<unknown>(
      identityApi,
      noraBase,
      `/conversations/${conversationId.current}`,
      { method: 'DELETE' },
    );
    setConversations(current =>
      current.filter(item => item.id !== conversationId.current),
    );
    startNewConversation();
  }, [identityApi, noraBase, startNewConversation]);

  const composer = (
    <Composer
      onSend={sendMessage}
      onStop={stopResponse}
      streaming={state.streaming}
      disabled={conversationLoading}
      models={models}
      modelError={modelError}
      selectedModel={selectedModel}
      onModelChange={setSelectedModel}
      attachments={attachments}
      onAddFiles={addFiles}
      onRemoveAttachment={removeAttachment}
      isMobile={isMobile}
      variant={state.messages.length === 0 ? 'hero' : 'docked'}
      context={activeContext}
      onRemoveContext={key =>
        setActiveContext(current => {
          const next = { ...current };
          delete next[key];
          const nextParams = new URLSearchParams(searchKey);
          nextParams.delete(key);
          setSearchParams(nextParams, { replace: true });
          return next;
        })
      }
    />
  );

  const returnTo = validatedReturnTo(searchParams);

  return (
    <div
      onDragEnter={event => {
        if (!event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        dragDepth.current += 1;
        setDragActive(true);
      }}
      onDragOver={event => {
        if (event.dataTransfer.types.includes('Files')) event.preventDefault();
      }}
      onDragLeave={event => {
        event.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragActive(false);
      }}
      onDrop={event => {
        event.preventDefault();
        dragDepth.current = 0;
        setDragActive(false);
        addFiles(imageFiles(event.dataTransfer));
      }}
      style={{
        position: embedded ? 'relative' : 'absolute',
        inset: embedded ? undefined : 0,
        width: embedded ? '100%' : undefined,
        height: embedded ? '100%' : undefined,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        overflow: 'hidden',
        background: COLOR.bg,
        color: COLOR.textPrimary,
        fontFamily: FONT.sans,
      }}
    >
      <DropOverlay active={dragActive} />

      <div
        style={{
          width: isMobile ? '100%' : embedded ? 48 : CHAT_RAIL_WIDTH,
          height: isMobile ? (embedded ? 46 : 56) : '100%',
          flexShrink: 0,
          display: 'flex',
          flexDirection: isMobile ? 'row' : 'column',
          alignItems: 'center',
          gap: 6,
          padding: isMobile ? '0 10px' : embedded ? '7px 0' : '14px 0',
          boxSizing: 'border-box',
          background: COLOR.bgRaised,
          borderRight: isMobile ? 'none' : `1px solid ${COLOR.borderSubtle}`,
          borderBottom: isMobile ? `1px solid ${COLOR.borderSubtle}` : 'none',
        }}
      >
        <div
          style={{
            margin: isMobile
              ? '0 auto 0 4px'
              : embedded
              ? '0 0 5px'
              : '0 0 14px',
            display: 'flex',
            userSelect: 'none',
          }}
          aria-label="Nora"
        >
          {NoraLogo}
        </div>
        <IconButton label="New chat" onClick={startNewConversation}>
          {NewChatIcon}
        </IconButton>
        <IconButton
          label="Search chats"
          active={view === 'search'}
          onClick={() => {
            setSearchQuery('');
            setView('search');
            fetchConversations();
          }}
        >
          {SearchIcon}
        </IconButton>
      </div>

      <main
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {view === 'chat' && (
          <div
            style={{
              minHeight: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: '0 18px',
              borderBottom: `1px solid ${COLOR.borderSubtle}`,
              background: COLOR.bgRaised,
              fontSize: FONT.size.sm,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {returnTo && !embedded && (
                <button
                  type="button"
                  onClick={() => navigate(returnTo)}
                  style={headerButton}
                >
                  ← Back to Kubernetes
                </button>
              )}
              {conversationMetadata?.parentConversationId && (
                <button
                  type="button"
                  onClick={() =>
                    openConversation(conversationMetadata.parentConversationId!)
                  }
                  style={headerButton}
                >
                  ↰ Parent
                </button>
              )}
              {(conversationMetadata?.branchCount ??
                conversationMetadata?.childConversationIds?.length ??
                0) > 0 && (
                <span style={{ color: COLOR.textMuted }}>
                  {conversationMetadata?.branchCount ??
                    conversationMetadata?.childConversationIds?.length}{' '}
                  branches
                </span>
              )}
            </div>
            <div
              className="nora-scroll"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                overflowX: 'auto',
              }}
            >
              <button
                type="button"
                aria-pressed={canvasOpen}
                onClick={() => setCanvasOpen(value => !value)}
                style={headerButton}
              >
                Canvas{artifacts.length ? ` · ${artifacts.length}` : ''}
              </button>
              <ConversationControls
                metadata={conversationMetadata}
                disabled={conversationLoading || state.streaming}
                onSettings={updateConversationSettings}
                onShare={shareConversation}
                onExport={exportConversation}
                onDelete={deleteConversation}
              />
              {embedded ? (
                <button
                  type="button"
                  onClick={() =>
                    window.open(window.location.href, '_blank', 'noopener')
                  }
                  style={headerButton}
                  title="Open Nora full screen"
                >
                  ↗ Full screen
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    // Generating a link always issues a fresh token and
                    // invalidates any link copied earlier (the token is only
                    // ever stored hashed, so the server can't just hand back
                    // the old one) — warn before silently breaking a link
                    // that may already be in someone else's hands.
                    if (
                      conversationMetadata?.visibility === 'shared' &&
                      !window.confirm(
                        'This conversation was already shared. Copying a new link will stop the previous link from working. Continue?',
                      )
                    ) {
                      return;
                    }
                    shareConversation().catch(error => {
                      setConversationError(
                        error instanceof Error ? error.message : String(error),
                      );
                    });
                  }}
                  style={headerButton}
                  title="Copy a link any signed-in user can open"
                >
                  Copy share link
                </button>
              )}
            </div>
          </div>
        )}
        {view === 'chat' && conversationError && (
          <div
            role="alert"
            style={{
              padding: '10px 18px',
              borderBottom: `1px solid ${COLOR.borderSubtle}`,
              color: COLOR.red,
              background: COLOR.bgRaised,
              fontFamily: FONT.sans,
              fontSize: FONT.size.sm,
            }}
          >
            {conversationError}
          </div>
        )}
        {view === 'search' ? (
          <ConversationSearch
            conversations={conversations}
            error={historyError}
            query={searchQuery}
            onClose={() => setView('chat')}
            onOpen={openConversation}
            onQueryChange={setSearchQuery}
          />
        ) : state.messages.length === 0 ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: isMobile ? '24px 16px 12vh' : '32px 32px 10vh',
              boxSizing: 'border-box',
              background:
                'radial-gradient(circle at 45% 48%, rgba(99,91,255,0.22) 0, rgba(17,24,42,0.72) 30%, rgba(11,16,32,0) 60%)',
            }}
          >
            <h1
              style={{
                margin: '0 0 30px',
                color: COLOR.textPrimary,
                fontFamily: FONT.sans,
                fontSize: isMobile ? '1.7rem' : '2.15rem',
                fontWeight: 400,
                textAlign: 'center',
                animation: ANIM.fadeIn,
              }}
            >
              {firstName
                ? `Hello ${firstName}, what can I help with?`
                : 'What can I help with?'}
            </h1>
            <div style={{ width: '100%', maxWidth: 720 }}>{composer}</div>
          </div>
        ) : (
          <>
            <MessageThread
              messages={state.messages}
              streaming={state.streaming}
              error={state.error}
              isMobile={isMobile}
              embedded={embedded}
              loading={conversationLoading}
              onPermissionDecision={respondToPermission}
              onBranchMessage={branchFromMessage}
            />
            <div style={{ width: '100%', maxWidth: 800, margin: '0 auto' }}>
              {composer}
            </div>
          </>
        )}
      </main>
      <OperationsCanvas
        artifacts={artifacts}
        open={canvasOpen && view === 'chat'}
        onClose={() => setCanvasOpen(false)}
        isMobile={isMobile}
      />
    </div>
  );
}

const headerButton: React.CSSProperties = {
  flexShrink: 0,
  border: 'none',
  background: 'transparent',
  color: COLOR.textSecondary,
  cursor: 'pointer',
  fontFamily: FONT.sans,
  fontSize: FONT.size.xs,
  padding: '8px 3px',
};

export function NoraPage({ embedded }: { embedded?: boolean }) {
  const isEmbedded =
    embedded ?? (typeof window !== 'undefined' && window.self !== window.top);
  return (
    <NoraProvider>
      <NoraChat embedded={isEmbedded} />
    </NoraProvider>
  );
}
